import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {formatCurrency, formatDate, formatPercent} from '@angular/common';
import {Router} from '@angular/router';
import {of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {SnackBarService} from '@core/services/snack-bar.service';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbUsdCompact} from '@features/admin-panel/helpers/admin-dashboard-format';
import {adbDrillMeta} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {
  AdminDashboardAttention,
  AdminDashboardAttentionItem,
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardSpendForecast,
  AdminDashboardTrialLikelihood,
  AdminDashboardTrialLikelihoods,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardAttentionResolved} from '@features/admin-panel/models/admin-dashboard-flags.models';
import {AdminDashboardAttentionTop} from '@features/admin-panel/models/admin-dashboard-attention.models';
import {
  AdminDashboardReportBlock,
  AdminDashboardReportProvider,
  AdminDashboardReportRow,
  AdminDashboardReportStat
} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {AdminDashboardSpendBarsComponent} from '../spend-bars/admin-dashboard-spend-bars.component';

/** The report stripe per health tier, from the band's tier colours (critical red, warning orange, healthy green). Watch is blue on screen and has no report colour, so it gets no stripe. */
const TIER_TONE: {[tier: string]: AdminDashboardReportRow['tone']} = {critical: 'red', warning: 'amber', healthy: 'green'};
/** The report stripe per trial confidence, from the chip colours (high green, medium orange). Low is grey on screen: no stripe. */
const CONFIDENCE_TONE: {[confidence: string]: AdminDashboardReportRow['tone']} = {high: 'green', medium: 'amber'};
/** `currency:'USD':'symbol':'1.0-0'`, as the band's template writes money. */
const usd = (value: number): string => formatCurrency(value, 'en-US', '$', 'USD', '1.0-0');
/** The nightly-pending sentence (keep-list wording): the band's pending state and the PDF's pending block. */
const PENDING_TEXT = 'Predictions are computed nightly — first results after tonight\'s run';
/** How many ranked rows show before "Show all" (D10, M/AttentionBand.tsx:29). */
const VISIBLE_ROWS = 8;
/** The flag tabs (D7, M/AttentionBand.tsx:216-229); `null` is All flags. The labels are the server's flag labels. */
const ATTENTION_TABS: ReadonlyArray<{kind: string | null; label: string}> = [
  {kind: null, label: 'All flags'},
  {kind: 'renewal', label: 'Renewal risk'},
  {kind: 'dark', label: 'Going dark'},
  {kind: 'usage', label: 'Usage falling'},
  {kind: 'spike', label: 'Spend spike'}
];
/** Flag kind → its Font Awesome icon on the row chips (M/AttentionBand.tsx:24-28; usage ITP-E-216-06). */
const FLAG_ICON: {[kind: string]: string} = {renewal: 'fa-file-contract', dark: 'fa-moon', usage: 'fa-arrow-trend-down', spike: 'fa-chart-line-up'};
/** Tier → the words on its pill (M/attention.ts:27). */
const TIER_LABEL: {[tier: string]: string} = {critical: 'Critical', warning: 'Warning', watch: 'Watch', healthy: 'Healthy'};

/** The dashboard's Needs attention band: flagged companies, ranked by tier then money at stake. */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-attention',
  templateUrl: './admin-dashboard-attention.component.html',
  styleUrls: ['./admin-dashboard-attention.component.scss']
})
export class AdminDashboardAttentionComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  @Input() spendForecastGranted = false;
  @Input() trialsGranted = false;
  /** The company whose why panel is open: its row is marked (D8). */
  @Input() selectedCompanyId: string | null = null;
  @Output() companySelected = new EventEmitter<string>();
  /** The nightly run's date (`AsOf`) each time the band's data arrives, null before the first run or after an error;
   *  the page shows it as "Data through" (ITP-E-148-05). */
  @Output() asOf = new EventEmitter<string | null>();
  /** The forecast card's spend bars while they are on screen (ITP-E-163-06): the PDF takes their picture. */
  @ViewChild(AdminDashboardSpendBarsComponent) spendBars?: AdminDashboardSpendBarsComponent;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  attention: AdminDashboardAttention | null = null;
  /** The band's query failed: it says so instead of looking pending. */
  failed = false;
  readonly failedTitle = 'Couldn\'t load Needs attention';
  forecast: AdminDashboardSpendForecast | null = null;
  trials: AdminDashboardTrialLikelihoods | null = null;
  kindFilter: string | null = null;
  exporting = false;
  /** The pending state's title (see PENDING_TEXT). */
  readonly pendingText = PENDING_TEXT;
  /** The ranked list shows every loaded row instead of the top 8 (D10). */
  showAll = false;
  /** The ranked list is sorted by money at stake instead of the server's tier-then-money order (D7). */
  sortByMoney = false;
  /** The Snoozed and handled group is open (ITP-E-164-05); closed by default. */
  resolvedOpen = false;
  readonly tabs = ATTENTION_TABS;
  readonly visibleRows = VISIBLE_ROWS;
  private loadSubscription: Subscription;
  private forecastSubscription: Subscription;
  private trialsSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private router: Router, private snackBarService: SnackBarService,
              private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params'] || changes['spendForecastGranted'] || changes['trialsGranted']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('attention', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.forecastSubscription?.unsubscribe();
    this.trialsSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-08). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (this.failed) {
      return [{kind: 'empty', title: 'Flagged companies', text: this.failedTitle}];
    }
    const attention = this.attention;
    if (!attention) {
      return [];
    }
    if (!attention.AsOf) {
      return [{kind: 'empty', title: 'Flagged companies', text: PENDING_TEXT}];
    }
    if (this.allClear) {
      return [{kind: 'empty', title: 'Flagged companies', text: 'Nothing needs attention'}];
    }
    const summary = attention.Summary;
    const renewal: AdminDashboardReportStat = this.scope !== 'NonClients'
      ? {label: 'Renewals at risk', value: String(summary?.renewalCount ?? 0), detail: `${usd(summary?.renewalMoney ?? 0)} at stake`}
      : {label: 'Renewals at risk', value: '—', detail: 'No Client Manager record'};
    const blocks: AdminDashboardReportBlock[] = [{
      kind: 'stats',
      title: 'Summary',
      subtitle: `as of ${formatDate(attention.AsOf, 'd MMM y', 'en-US', 'UTC')}`,
      stats: [
        renewal,
        {label: 'Going dark', value: String(summary?.darkCount ?? 0), detail: `${usd(summary?.darkMoney ?? 0)} at stake`},
        {label: 'Usage falling', value: String(summary?.usageCount ?? 0), detail: `${usd(summary?.usageMoney ?? 0)} at stake`},
        {label: 'Spend spikes', value: String(summary?.spikeCount ?? 0), detail: `${usd(summary?.spikeMoney ?? 0)} spent`}
      ]
    }];
    if (this.forecast) {
      const forecast = this.forecast;
      const stats: AdminDashboardReportStat[] = [{label: 'On track for', value: usd(forecast.forecast)}];
      if (forecast.vsLastMonth !== null) {
        stats.push({label: 'vs last month', value: `${forecast.vsLastMonth > 0 ? '+' : ''}${formatPercent(forecast.vsLastMonth, 'en-US', '1.0-0')}`});
      }
      stats.push({label: 'Spikes flagged', value: String(forecast.spikes)});
      blocks.push({kind: 'stats', title: 'Spend forecast', stats});
      const bars = this.spendBars?.reportImage();
      if (bars) {
        blocks.push({kind: 'chart', title: 'Daily XatBot spend, last 30 days', image: bars.image, widthPx: bars.widthPx, heightPx: bars.heightPx});
      }
    }
    if (this.trials) {
      const trials = this.trials.Items ?? [];
      if (!trials.length) {
        blocks.push({kind: 'empty', title: 'Likely trial conversions', text: 'No trials running'});
      } else {
        const table: AdminDashboardReportBlock = {
          kind: 'table',
          title: 'Likely trial conversions',
          columns: [
            {header: 'Company', weight: 2},
            {header: 'Days left', align: 'right'},
            {header: 'Likelihood', align: 'right'},
            {header: 'Confidence'}
          ],
          rows: trials.slice(0, 5).map((trial): AdminDashboardReportRow => {
            const cells = [trial.companyName, `${trial.daysLeft}d left`, formatPercent(trial.rate, 'en-US', '1.0-0'), trial.confidence];
            const tone = CONFIDENCE_TONE[trial.confidence];
            return tone ? {cells, tone} : {cells};
          })
        };
        if (this.trials.Total > 5) {
          table.note = `+${this.trials.Total - 5} more`;
        }
        blocks.push(table);
      }
    }
    const items = this.items;
    const flagged: AdminDashboardReportBlock = items.length
      ? {
        kind: 'table',
        title: 'Flagged companies',
        columns: [
          {header: 'Health', align: 'right', weight: 0.7},
          {header: 'Company', weight: 2},
          {header: 'Flags', weight: 1.6},
          {header: 'Reason', weight: 3},
          {header: 'At stake', align: 'right', weight: 1.2}
        ],
        rows: items.map((item): AdminDashboardReportRow => {
          const cells = [
            String(item.healthScore ?? ''),
            item.companyName,
            item.flags.map(flag => flag.label).join(', '),
            item.flags[0]?.reason ?? '',
            item.moneyKnown ? usd(item.money) : (item.moneyLabel ?? '')
          ];
          const tone = TIER_TONE[item.tier || 'healthy'];
          return tone ? {cells, tone} : {cells};
        })
      }
      : {kind: 'empty', title: 'Flagged companies', text: 'Nothing needs attention'};
    if (attention.Total > items.length) {
      flagged.note = `Showing ${items.length} of ${attention.Total}`;
    }
    blocks.push(flagged);
    return blocks;
  }

  /**
   * The section summary (D1, M/page.tsx:173-178): "2 flagged companies · $120k ARR at stake. Computed nightly …".
   * The ARR is the server's `arrAtStake` (ITP-E-163-01): each client with an open renewal or going-dark flag counted
   * once. Without it (an older server), the R4 sum of the renewal, going-dark and usage-falling money, marked "up to" because a
   * company with both flags counts in both sums. Non-clients have no ARR, so the money part is left out there, and
   * whenever it is 0.
   */
  get summaryText(): string {
    const attention = this.attention;
    if (!attention?.AsOf) {
      return '';
    }
    const companies = countOf(attention.Total, 'flagged company', 'flagged companies');
    const summary = attention.Summary;
    const exact = summary?.arrAtStake;
    const arr = this.scope === 'NonClients'
      ? 0
      : typeof exact === 'number' ? exact : (summary?.renewalMoney ?? 0) + (summary?.darkMoney ?? 0) + (summary?.usageMoney ?? 0);
    const stake = arr > 0 ? ` · ${typeof exact === 'number' ? '' : 'up to '}${adbUsdCompact(arr)} ARR at stake` : '';
    return `${companies}${stake}. Computed nightly from the prediction rules; every flag says why.`;
  }

  /**
   * The nightly run happened and flagged nothing at all: the whole band — header, cards, tabs and list — is one compact
   * line, on screen and in the PDF (ITP-E-173-05, owner decision ITP-E-173-06). Snoozed or handled flags are still
   * flags: while there are any, the band stays, saying every flag has been dealt with above the Snoozed and handled
   * group, so a flag can be reopened (ITP-E-164-05/06).
   */
  get allClear(): boolean {
    return !!this.attention?.AsOf && !this.attention.Items?.length
      && !this.attention.Resolved?.length && !this.attention.ResolvedTotal;
  }

  get items(): AdminDashboardAttentionItem[] {
    const all = this.attention?.Items ?? [];
    const filtered = this.kindFilter ? all.filter(i => i.flags.some(f => f.kind === this.kindFilter)) : all;
    return this.sortByMoney ? [...filtered].sort((a, b) => b.money - a.money) : filtered;
  }

  /**
   * The Snoozed and handled group's rows (ITP-E-164-05, OCA-E-159 AttentionBand.tsx:96-101): the server's `Resolved`
   * (every flag snoozed or handled), newest action first, narrowed by the flag tab like the ranked list. Empty from an
   * older server.
   */
  get resolvedItems(): AdminDashboardAttentionResolved[] {
    const all = this.attention?.Resolved ?? [];
    return this.kindFilter ? all.filter(item => item.flags.some(flag => flag.kind === this.kindFilter)) : all;
  }

  /** The group's count: every such company on the server (`ResolvedTotal`) on All flags; the loaded rows of a tab's kind. */
  get resolvedCount(): number {
    return this.kindFilter ? this.resolvedItems.length : (this.attention?.ResolvedTotal ?? this.resolvedItems.length);
  }

  /** Opens or closes the Snoozed and handled group (closed by default; it stays as the user left it across reloads). */
  toggleResolved(): void {
    this.resolvedOpen = !this.resolvedOpen;
  }

  /** A resolved row's icon in place of its rank: a snooze clock, or a check for handled (AttentionBand.tsx:293). */
  resolvedIcon(item: AdminDashboardAttentionResolved): string {
    return item.status === 'snoozed' ? 'fa-alarm-snooze' : 'fa-circle-check';
  }

  /**
   * "Snoozed until 1 Nov by Ann Admin · 2 Oct 2026, 09:40 UTC — “Renewal in procurement”" /
   * "Handled by Bo Admin · 28 Sep 2026, 15:05 UTC" (AttentionBand.tsx:322-327); the note part only when there is one.
   */
  resolvedText(item: AdminDashboardAttentionResolved): string {
    const what = item.status === 'snoozed' && item.snoozedUntil
      ? `Snoozed until ${formatDate(item.snoozedUntil, 'd MMM', 'en-US', 'UTC')}`
      : 'Handled';
    const when = `${formatDate(item.at, 'd MMM y, HH:mm', 'en-US', 'UTC')} UTC`;
    const note = item.note ? ` — “${item.note}”` : '';
    return `${what} by ${item.byName} · ${when}${note}`;
  }

  /** The empty list's title: only snoozed / handled flags are left in view, or nothing is flagged (ITP-E-164-06). */
  get emptyTitle(): string {
    return this.resolvedItems.length ? 'Every flag here has been dealt with' : 'Nothing needs attention';
  }

  /** The empty list's sentence (OCA-E-159 AttentionBand.tsx:243-256). */
  get emptyBody(): string {
    return this.resolvedItems.length
      ? 'The snoozed and handled flags are listed below; a snooze returns to the list when it runs out.'
      : 'No company meets a prediction rule in the current filters.';
  }

  /** The rows on screen: the top 8 until "Show all" (D10). */
  get visibleItems(): AdminDashboardAttentionItem[] {
    const items = this.items;
    return this.showAll ? items : items.slice(0, VISIBLE_ROWS);
  }

  /** How many flagged companies the current tab covers on the server: the "Showing X of N" total. */
  get listTotal(): number {
    return this.kindCount(this.kindFilter);
  }

  /** Flagged companies per kind (`null` = all) from the server's Summary; without a Summary, from the loaded rows. */
  kindCount(kind: string | null): number {
    const attention = this.attention;
    if (!attention) {
      return 0;
    }
    if (kind === null) {
      return attention.Total;
    }
    const summary = attention.Summary;
    if (summary) {
      return kind === 'renewal' ? summary.renewalCount : kind === 'dark' ? summary.darkCount
        : kind === 'usage' ? (summary.usageCount ?? 0) : kind === 'spike' ? summary.spikeCount : 0;
    }
    return (attention.Items ?? []).filter(item => item.flags.some(flag => flag.kind === kind)).length;
  }

  /** A flag tab: show one kind (`null` = all) and go back to the top 8. */
  showKind(kind: string | null): void {
    this.kindFilter = kind;
    this.showAll = false;
  }

  toggleSort(): void {
    this.sortByMoney = !this.sortByMoney;
  }

  toggleShowAll(): void {
    this.showAll = !this.showAll;
  }

  /** Money at stake per kind from the Summary (renewal, going dark and usage falling: ARR; spikes: the spike days' spend). */
  kindMoney(kind: string): number {
    const summary = this.attention?.Summary;
    if (summary) {
      return kind === 'renewal' ? summary.renewalMoney : kind === 'dark' ? summary.darkMoney
        : kind === 'usage' ? (summary.usageMoney ?? 0) : kind === 'spike' ? summary.spikeMoney : 0;
    }
    return (this.attention?.Items ?? [])
      .filter(item => item.moneyKnown && item.flags.some(flag => flag.kind === kind))
      .reduce((sum, item) => sum + item.money, 0);
  }

  /** "$120k ARR": the renewal, going-dark and usage-falling cards' unit (D3); empty in Non-clients, which have no ARR. */
  arrUnit(kind: string): string {
    return this.scope === 'NonClients' ? '' : `${adbUsdCompact(this.kindMoney(kind))} ARR`;
  }

  /** Compact USD for the template: $950, $9.8k, $120k, $3.46M. */
  usdCompact(value: number): string {
    return adbUsdCompact(value ?? 0);
  }

  /** "Show 12 accounts in the list" / "Show 1 spike in the list": a card's footer link (D3). */
  showInListText(kind: string): string {
    return `Show ${countOf(this.kindCount(kind), kind === 'spike' ? 'spike' : 'account')} in the list`;
  }

  /** A card's footer link: select that kind's tab and bring the ranked list into view. */
  showInList(kind: string): void {
    this.showKind(kind);
    this.host.nativeElement.querySelector('[data-testid="attention-flags"]')?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  /**
   * The company a card names for a kind (D4): the server's pick over every flagged row (`Summary.topRenewal` /
   * `topDark` / `topSpike`, ITP-E-163-01; null = no open flag of that kind). Without it (an older server), the loaded
   * row with the most money at stake for that kind, which is approximate past the server's 50-row page.
   */
  topOf(kind: string): AdminDashboardAttentionTop | null {
    const summary = this.attention?.Summary;
    const fromServer = kind === 'renewal' ? summary?.topRenewal
      : kind === 'dark' ? summary?.topDark
        : kind === 'usage' ? summary?.topUsage
          : kind === 'spike' ? summary?.topSpike
            : undefined;
    if (fromServer !== undefined) {
      return fromServer;
    }
    let top: AdminDashboardAttentionItem | null = null;
    for (const item of this.attention?.Items ?? []) {
      if (item.flags.some(flag => flag.kind === kind) && (!top || item.money > top.money)) {
        top = item;
      }
    }
    return top ? {companyId: top.companyId, companyName: top.companyName, reason: this.reasonOf(top, kind)} : null;
  }

  /** The reason of a row's flag of this kind. */
  reasonOf(item: AdminDashboardAttentionItem, kind: string): string {
    return item.flags.find(flag => flag.kind === kind)?.reason ?? '';
  }

  /** A company named on a card opens its why panel, as a row does. */
  selectCompany(companyId: string): void {
    this.companySelected.emit(companyId);
  }

  /** Trials at 50% or more (D6, M/AttentionBand.tsx:118), most likely first. */
  get likelyTrials(): AdminDashboardTrialLikelihood[] {
    return (this.trials?.Items ?? []).filter(trial => trial.rate >= 0.5).sort((a, b) => b.rate - a.rate);
  }

  /**
   * "Ann Lee · 12 seats" / "Not in Client Manager": who owns the account, after the row's flag chips (D9; owner
   * decision Q2: owner and seats instead of country), worded by the shared drill meta line (ITP-E-159). '' for a
   * client row without an owner — an older server that does not send one (ITP-E-163-07).
   */
  ownerMeta(item: AdminDashboardAttentionItem): string {
    if (item.isClient && !item.ownerName) {
      return '';
    }
    return adbDrillMeta({
      companyId: item.companyId,
      companyName: item.companyName,
      ownerName: item.isClient ? item.ownerName : null,
      seats: item.isClient ? item.seats ?? null : null
    });
  }

  flagIcon(kind: string): string {
    return FLAG_ICON[kind] ?? 'fa-flag';
  }

  tierLabel(tier: string | null): string {
    return TIER_LABEL[tier || 'healthy'] ?? 'Healthy';
  }

  /** "Spend forecast · Oct" (M/AttentionBand.tsx:168): the forecast month from the server's `monthStart` (ITP-E-163-04); "Spend forecast" without it. */
  get forecastTitle(): string {
    const month = this.forecast?.monthStart;
    return month ? `Spend forecast · ${formatDate(month, 'MMM', 'en-US', 'UTC')}` : 'Spend forecast';
  }

  /**
   * "+18% vs Sep" / "−6% vs Dec" (U+2212): the forecast card's unit (D5), against the month before `monthStart`
   * (ITP-E-163-04); "… vs last month" without it; '' without a last month.
   */
  get forecastChange(): string {
    const change = this.forecast?.vsLastMonth;
    if (change === null || change === undefined) {
      return '';
    }
    const month = this.forecast?.monthStart;
    let versus = 'last month';
    if (month) {
      const start = new Date(month);
      versus = formatDate(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 1, 1)), 'MMM', 'en-US', 'UTC');
    }
    return `${change >= 0 ? '+' : '−'}${formatPercent(Math.abs(change), 'en-US', '1.0-0')} vs ${versus}`;
  }

  select(item: AdminDashboardAttentionItem): void {
    this.companySelected.emit(item.companyId);
  }

  open360(id: string, event: Event): void {
    event.stopPropagation();
    this.router.navigateByUrl('/admin-panel/company-360/' + id);
  }

  /** Downloads every flagged company in scope (snoozed and handled ones included) as an Excel workbook. */
  exportExcel(): void {
    if (this.exporting || !this.params) {
      return;
    }
    this.exporting = true;
    this.dataService.exportAttention(this.params).subscribe({
      next: result => {
        this.exporting = false;
        this.snackBarService.showInformation(
          `${result.fileName} downloaded — ${countOf(result.rows, 'flagged company', 'flagged companies')}, with tier, reasons and state.`);
      },
      error: (error: unknown) => {
        this.exporting = false;
        this.snackBarService.showError(error instanceof Error && error.message
          ? error.message
          : 'Could not export the attention list. Try again.');
      }
    });
  }

  load(): void {
    this.kindFilter = null;
    this.showAll = false;
    this.loadSubscription?.unsubscribe();
    this.forecastSubscription?.unsubscribe();
    this.trialsSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.attention && !this.failed) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getAttention(this.params).subscribe({
      next: attention => this.show(attention, false),
      error: () => this.show(null, true)
    });
    if (this.spendForecastGranted) {
      this.forecastSubscription = this.dataService.getData<AdminDashboardSpendForecast>(
        'AdminDashboardSpendForecastQuery', this.params, 'Forecast'
      ).pipe(
        catchError(() => of(null))
      ).subscribe(forecast => this.forecast = forecast);
    } else {
      this.forecast = null;
    }
    if (this.trialsGranted) {
      this.trialsSubscription = this.dataService.getTrialLikelihoods(this.params).pipe(
        catchError(() => of(null))
      ).subscribe(trials => this.trials = trials);
    } else {
      this.trials = null;
    }
  }

  /**
   * Before the first nightly run the band has nothing to show (empty). An all-clear is information on Clients, but
   * nothing worth showing on Non-clients, where the dashboard hides it (owner decision, ITP-E-173-05).
   */
  private show(attention: AdminDashboardAttention | null, failed: boolean): void {
    this.attention = attention;
    this.failed = failed;
    this.asOf.emit(attention?.AsOf ?? null);
    if (failed) {
      this.report('failed');
    } else if (!attention?.AsOf || (this.allClear && this.scope === 'NonClients')) {
      this.report('empty');
    } else {
      this.report('content');
    }
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
